import classNames from 'classnames'
import * as React from 'react'
import { Tooltip as RechartsTooltip } from 'recharts'

import { ChartWrapperContext } from './ChartWrapper'
import { CursorLine } from './Cursor'
import { CHART_ITEM_COLORS, ChartItemColorType } from './constants'

type TooltipPayload<PayloadType, ValueType> = {
  color?: string
  name?: string | number
  value?: ValueType
  unit?: React.ReactNode
  dataKey?: string | number
  payload?: PayloadType
  chartType?: string
  className?: string
}

type TooltipValueFormatter<ValueType, PayloadType> = (
  value: ValueType,
  extra: {
    dataKey: string
    payload?: PayloadType & { dataKey: string }
    fullTooltipPayload: Array<TooltipPayload<PayloadType, any>>
  }
) => React.ReactNode

interface TooltipRowProps<ValueType, PayloadType> {
  isActive?: boolean
  label: React.ReactNode
  dataKey: string
  dataKeyPayload?: any
  fullTooltipPayload?: any
  indicator?: ChartItemColorType | JSX.Element
  formatValue?: TooltipValueFormatter<ValueType, PayloadType>
  indicatorClass?: string
  labelClass?: string
  valueClass?: string
}

export function TooltipRow<ValueType, PayloadType>({
  isActive,
  indicator,
  label,
  dataKey,
  dataKeyPayload,
  fullTooltipPayload,
  formatValue,
  indicatorClass,
  labelClass,
  valueClass,
}: TooltipRowProps<ValueType, PayloadType>) {
  return (
    <>
      <div className={classNames(indicatorClass, { 'mr-2': !!indicator })}>
        {!indicator || React.isValidElement(indicator) ? (
          indicator
        ) : (
          <div
            className={classNames(
              'relative -top-px h-1.5 w-1.5 rounded-full bg-current',
              CHART_ITEM_COLORS[indicator as ChartItemColorType] ?? CHART_ITEM_COLORS.blue
            )}
          />
        )}
      </div>
      <div
        className={classNames('label-with-bold-weight', labelClass, { 'text-default font-medium': isActive })}
        data-text={label}
      >
        {label}
      </div>
      <div className={classNames(valueClass, 'ml-2 text-right tabular-nums text-default')}>
        {formatValue?.(dataKeyPayload?.value, { dataKey, payload: dataKeyPayload ?? {}, fullTooltipPayload }) ??
          dataKeyPayload?.value}
      </div>
    </>
  )
}

interface TooltipInnerProps<ValueType, PayloadType> extends React.ComponentProps<typeof RechartsTooltip> {
  className?: string
  showLabel?: boolean
  formatLabel?: (value: any, payload?: Array<TooltipPayload<PayloadType, any>>) => React.ReactNode
  formatValue?: TooltipValueFormatter<ValueType, PayloadType>
}

export function TooltipInner<ValueType, PayloadType>({
  payload,
  label,
  formatLabel,
  formatValue,
  showLabel = !!label,
  ...props
}: React.PropsWithChildren<TooltipInnerProps<ValueType, PayloadType>>) {
  const { isTooltipActive, activeKey } = React.useContext(ChartWrapperContext)

  return (
    <div
      className={classNames(
        props.className,
        'rounded-md border border-secondary bg-primary px-3.5 pb-2.5 pt-3.5 text-left font-sans text-2xs leading-4 text-muted-2 shadow-md',
        { 'opacity-0': !isTooltipActive }
      )}
    >
      {showLabel && (
        <div className="mb-1.5 text-sm text-default font-medium dark:text-gray-200">
          {formatLabel?.(label, payload) ?? label}
        </div>
      )}

      <div className="grid grid-cols-[max-content,1fr,max-content] items-baseline gap-y-2">
        {React.Children.map(props.children, (child) => {
          if (!React.isValidElement(child) || child.type !== TooltipRow) return child

          const dataKeyPayload = payload?.find((payload) => payload.dataKey === child.props.dataKey)

          return React.cloneElement(child, {
            isActive: child.props.isActive ?? activeKey === child.props.dataKey,
            dataKeyPayload,
            fullTooltipPayload: payload,
            formatValue: child.props.formatValue ?? formatValue,
          } as TooltipRowProps<unknown, unknown>)
        })}
      </div>
    </div>
  )
}

export class Tooltip<ValueType, PayloadType> extends React.Component<
  React.PropsWithChildren<TooltipInnerProps<ValueType, PayloadType>>
> {
  static displayName = RechartsTooltip.displayName

  static defaultProps = {
    ...RechartsTooltip.defaultProps,
    offset: 40,
    position: { y: 4 },
    isAnimationActive: false,
    wrapperStyle: { outline: 'none', zIndex: 10 },
    className: 'min-w-70',
    cursor: <CursorLine />,
  }

  render() {
    const { className, showLabel, formatLabel: formatTitle, formatValue, children, ...props } = this.props

    return (
      <RechartsTooltip
        content={
          <TooltipInner<ValueType, PayloadType>
            className={className}
            formatLabel={formatTitle}
            formatValue={formatValue}
            showLabel={showLabel}
          >
            {children}
          </TooltipInner>
        }
        {...props}
      />
    )
  }
}
